<div class="bg-white dark:bg-secondary-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
  <!-- Top section -->
  <div class="flex items-start justify-between gap-4">
    <!-- Left: Avatar + Info -->
    <div class="flex items-start gap-3.5 min-w-0">
      <!-- Avatar -->
      <img *ngIf="lead.photoUrl" [src]="lead.photoUrl" [alt]="fullName"
           class="w-12 h-12 rounded-full object-cover flex-shrink-0">
      <div *ngIf="!lead.photoUrl" [class]="lead.avatarColor + ' w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-semibold flex-shrink-0'">
        {{ initials }}
      </div>
      <!-- Info -->
      <div class="min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <h3 class="text-base font-semibold text-[#3c4377] dark:text-primary-200 truncate leading-snug">{{ fullName }}</h3>
          <span *ngIf="lead.status === 'replied'" class="text-xs text-gray-400 dark:text-gray-500">Replied</span>
          <span *ngIf="lead.status === 'opened'" class="text-xs text-gray-400 dark:text-gray-500">Opened {{ lead.openedCount ? lead.openedCount + 'x' : '' }}</span>
        </div>
        <p class="text-sm text-[#3c4377] dark:text-primary-200 truncate mt-0.5">{{ lead.jobTitle }}</p>
        <p *ngIf="lead.location" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ lead.location }}</p>
        <p *ngIf="lead.sentAt" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Sent {{ lead.sentAt }}</p>
      </div>
    </div>

    <!-- Right: Status badge + date -->
    <div class="flex flex-col items-end gap-1.5 flex-shrink-0">
      <span *ngIf="isNew"
            class="text-xs font-medium bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 rounded-md px-2 py-1">
        New
      </span>
      <span *ngIf="statusLabel"
            class="text-xs font-medium bg-primary/5 text-primary rounded-md px-2 py-1">
        {{ statusLabel }}
      </span>
      <span *ngIf="lead.createdAt" class="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
        {{ lead.createdAt | date:'MMM d, y' }}
      </span>
    </div>
  </div>

  <!-- Function at Company -->
  <div class="mt-3 inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-secondary-700 px-3 py-1.5 rounded-lg">
    <ng-container *ngIf="lead.departments && lead.departments.length > 0">{{ lead.departments.join(', ') }} &#64; </ng-container>
    <img *ngIf="lead.companyLogoUrl" [src]="lead.companyLogoUrl" [alt]="lead.company"
         class="w-6 h-6 rounded-sm object-contain flex-shrink-0">
    <span class="font-medium">{{ lead.company }}</span>
  </div>

  <!-- Why this lead? -->
  <div class="mt-3">
    <button (click)="toggleWhyThisLead()" class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5 text-amber-400">
        <path d="M8 1a4.5 4.5 0 0 0-1.678 8.677l.474 2.612a.5.5 0 0 0 .492.411h1.424a.5.5 0 0 0 .492-.41l.474-2.613A4.5 4.5 0 0 0 8 1ZM6.25 13.5a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5h-3.5Z" />
      </svg>
      Why this lead?
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3 h-3 transition-transform duration-200" [class.rotate-180]="showWhyThisLead">
        <path fill-rule="evenodd" d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
      </svg>
    </button>
    <div *ngIf="showWhyThisLead && lead.whyThisLead" class="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-secondary-700 rounded-lg p-3 transition-all">
      {{ lead.whyThisLead }}
    </div>
  </div>

  <!-- Bottom: Actions -->
  <div class="mt-4 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <!-- View Report (report generated) -->
      <button *ngIf="hasReport" (click)="onViewProfile()"
              class="btn btn-blue">
        View Report
      </button>
      <!-- Generating (in progress) -->
      <button *ngIf="!hasReport && isGenerating" disabled
              class="btn btn-blue cursor-not-allowed opacity-60">
        <span class="flex items-center gap-1.5">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Generating...
        </span>
      </button>
      <!-- Generate Profile (default) -->
      <button *ngIf="!hasReport && !isGenerating" (click)="onViewProfile()" [disabled]="!lead.linkedinUrl"
              [class]="lead.linkedinUrl
                ? 'btn btn-blue'
                : 'btn btn-blue cursor-not-allowed opacity-60'">
        Generate Profile
      </button>
      <button (click)="onViewDetails()" class="btn btn-cancel">
        Details
      </button>
    </div>
    <button disabled class="btn btn-cancel cursor-not-allowed opacity-60">
      Send Email
    </button>
  </div>
</div>
